import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronLeft, ChevronRight, Calendar as CalIcon } from 'lucide-react';

const DAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const TYPE_COLORS = { urlaub: 'bg-blue-500', krank: 'bg-rose-500', schulung: 'bg-violet-500', weiterbildung: 'bg-violet-500' };

export default function KalenderUebersicht() {
  const { API } = useAuth();
  const [currentMonth, setCurrentMonth] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; });
  const [data, setData] = useState({ assignments: [], vacations: [], users: {}, projects: {} });

  useEffect(() => {
    axios.get(`${API}/calendar?month=${currentMonth}`).then(r => setData(r.data)).catch(() => {});
  }, [API, currentMonth]);

  const [year, month] = currentMonth.split('-').map(Number);
  const firstDay = new Date(year, month - 1, 1);
  const daysInMonth = new Date(year, month, 0).getDate();
  const startOffset = (firstDay.getDay() + 6) % 7;
  const today = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })();

  const prevMonth = () => {
    const d = new Date(year, month - 2, 1);
    setCurrentMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`);
  };
  const nextMonth = () => {
    const d = new Date(year, month, 1);
    setCurrentMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`);
  };

  const monthName = new Date(year, month - 1).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });

  const getEventsForDay = (dateStr) => {
    const events = [];
    data.assignments.filter(a => a.date === dateStr).forEach(a => {
      events.push({ type: 'assignment', label: data.users[a.user_id] || '?', sub: data.projects[a.project_id] || '', color: 'bg-amber-500', time: `${a.start_time}-${a.end_time}` });
    });
    data.vacations.filter(v => v.start_date <= dateStr && v.end_date >= dateStr).forEach(v => {
      events.push({ type: 'vacation', label: v.user_name, sub: v.type, color: TYPE_COLORS[v.type] || 'bg-blue-500' });
    });
    return events;
  };

  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div className="space-y-4 animate-fade-in" data-testid="kalender-page">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-white tracking-tight">Kalender</h1>
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={prevMonth} className="text-slate-400 hover:text-white"><ChevronLeft className="w-5 h-5" /></Button>
          <span className="text-lg font-semibold text-white min-w-[180px] text-center capitalize">{monthName}</span>
          <Button variant="ghost" size="icon" onClick={nextMonth} className="text-slate-400 hover:text-white"><ChevronRight className="w-5 h-5" /></Button>
        </div>
      </div>

      <div className="flex gap-3 text-xs text-slate-500">
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-amber-500" />Einsatz</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-blue-500" />Urlaub</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-rose-500" />Krank</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-violet-500" />Schulung</span>
      </div>

      <div className="bg-slate-900/50 border border-slate-800/60 rounded-sm overflow-hidden">
        <div className="grid grid-cols-7">
          {DAYS.map(d => (
            <div key={d} className="px-2 py-2 text-center text-xs font-mono uppercase text-slate-500 tracking-wider border-b border-slate-800/40 bg-slate-900/80">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((day, i) => {
            if (!day) return <div key={i} className="min-h-[90px] border-b border-r border-slate-800/30 bg-slate-950/30" />;
            const dateStr = `${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
            const isToday = dateStr === today;
            const isWeekend = (i % 7) >= 5;
            const events = getEventsForDay(dateStr);
            return (
              <div key={i} className={`min-h-[90px] border-b border-r border-slate-800/30 p-1 ${isToday ? 'bg-amber-500/5' : isWeekend ? 'bg-slate-950/20' : ''}`}>
                <span className={`text-xs font-mono ${isToday ? 'text-amber-400 font-bold' : 'text-slate-500'}`}>{day}</span>
                <div className="mt-1 space-y-0.5">
                  {events.slice(0, 3).map((ev, j) => (
                    <div key={j} className={`${ev.color} rounded-sm px-1 py-0.5 text-[9px] text-white truncate leading-tight`}>
                      {ev.label}{ev.sub ? ` · ${ev.sub}` : ''}
                    </div>
                  ))}
                  {events.length > 3 && <span className="text-[9px] text-slate-500">+{events.length - 3} mehr</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
